import React from 'react';

import {
  ACTIVE,
  CANCELLED,
  EUR,
  PENDING,
  PROCESSING,
  REQUEST_FAILED,
  REQUEST_SUCCESS,
  STRIPE_METHOD_CHECKOUT,
  STRIPE_METHOD_DIRECT,
  USAGE_LICENSED,
  USAGE_METERED,
  USD,
  BILLING_MODE_MONTH,
  BILLING_MODE_UNIQUE,
  BILLING_MODE_YEAR,
  INTERVAL_COUNT_FOR_BILLING_MONTH,
  INTERVAL_COUNT_FOR_BILLING_YEAR,
  LANG_EN,
  LANG_ES,
  LANG_IT,
  LANG_FR,
  LANG_PT,
  PRODUCT_CATEGORY_TYPE_EMAIL,
  PRODUCT_CATEGORY_TYPE_SMS,
  PRODUCT_CATEGORY_TYPE_PREPAID,
  PRODUCT_CATEGORY_PREPAID_SMS,
  PRODUCT_CATEGORY_PREPAID_EMAIL,
  PRODUCT_CATEGORY_LITE,
  PRODUCT_CATEGORY_PRO,
  PRODUCT_CATEGORY_TOTAL,
  PRODUCT_CATEGORY_FREEMIUM,
  PRODUCT_CATEGORY_APPSUMO,
  INTERVAL_COUNT_FOR_BILLING_UNIQUE,
  AUTOMATIC_COUPON,
  INCOMPLETE,
  PRODUCT_CATEGORY_SUSPENDED,
} from '@/config/appConstants';

export type AppLanguages =
  | typeof LANG_EN
  | typeof LANG_ES
  | typeof LANG_IT
  | typeof LANG_FR
  | typeof LANG_PT;

export type BaseEntity = {
  id: string;
  createdAt: string;
};

export interface BaseFormEntity {
  formName?: string;
}

export interface ICurrencyKeys {
  eur: string;
  usd: string;
}

export type Currency = typeof EUR | typeof USD;

export type BillingMode =
  | typeof BILLING_MODE_MONTH
  | typeof BILLING_MODE_YEAR
  | typeof BILLING_MODE_UNIQUE;

export const BillingModes = [BILLING_MODE_MONTH, BILLING_MODE_YEAR, BILLING_MODE_UNIQUE];

export type IntervalBillingMode =
  | typeof INTERVAL_COUNT_FOR_BILLING_MONTH
  | typeof INTERVAL_COUNT_FOR_BILLING_YEAR
  | typeof INTERVAL_COUNT_FOR_BILLING_UNIQUE;

export const IntervalBillingModes = [
  INTERVAL_COUNT_FOR_BILLING_MONTH,
  INTERVAL_COUNT_FOR_BILLING_YEAR,
];

export type SliderMarkType = {
  [key: number]: string;
};

export type ParsedSliderMarkType = {
  [key: number]: React.ReactNode;
};

export const currencySymbolMap: ICurrencyKeys = {
  eur: '€',
  usd: 'US$',
};

export type FieldOptions = {
  showLabel?: boolean;
  customClasses?: string;
  name?: any;
  type?: string;
  value?: any;
  disabled?: boolean;
  placeholder?: string;
  onChange?: any;
  onBlur?: any;
  itemClasses?: string;
  options?: any[];
  renderLabel?: any;
  required?: boolean;
  defaultCountry?: any;
  userLanguage?: AppLanguages;
  helpText?: React.ReactNode | string;
  autoFocus?: boolean;
};

export type UsageType = typeof USAGE_LICENSED | typeof USAGE_METERED;

export interface ProductFeaturesTypes {
  title: string;
  bullets: string[];
}

export interface ProductDescriptionTypes {
  productName: string;
  productStep: string;
  productStepCriteria: string;
  categoryName: string;
  categoryDescription: string;
  actionButton: string;
  features: ProductFeaturesTypes;
}

export interface RecurringTypes {
  aggregate_usage?: string | null;
  interval: string;
  interval_count: number;
  trial_period_days?: string | null;
  usage_type: string;
}

export interface PricesTypes {
  id: string;
  currency: string;
  amount: string | number;
  recurring: RecurringTypes | null;
  productId?: string;
  usageBillingThreshold: number;
  promotionCode?: PromotionCodeTypes | null;
  amountWithPromotion?: string | number;
}

export type PromotionCodeTypes = {
  id: string;
  coupon: CouponTypes;
  code: string | null;
};

export type ProductCategoryTypes =
  | typeof PRODUCT_CATEGORY_TYPE_EMAIL
  | typeof PRODUCT_CATEGORY_TYPE_SMS
  | typeof PRODUCT_CATEGORY_TYPE_PREPAID;

export type CategoryType =
  | typeof PRODUCT_CATEGORY_PREPAID_SMS
  | typeof PRODUCT_CATEGORY_PREPAID_EMAIL
  | typeof PRODUCT_CATEGORY_LITE
  | typeof PRODUCT_CATEGORY_PRO
  | typeof PRODUCT_CATEGORY_TOTAL
  | typeof PRODUCT_CATEGORY_FREEMIUM
  | typeof PRODUCT_CATEGORY_APPSUMO
  | typeof PRODUCT_CATEGORY_SUSPENDED
  | string;

export interface ProductTypes {
  id: string;
  mdirectorId: string;
  class: string;
  category: CategoryType;
  hidePrice?: boolean | undefined;
  featured: boolean;
  description: string | null;
  info: ProductDescriptionTypes;
  prices: PricesTypes[];
  name: string;
}

export interface UserProductTypes {
  product: ProductTypes;
  groupProduct: GroupProductTypes;
  invoicePreview: InvoicePreviewTypes | null;
}

export type StatusType =
  | typeof PENDING
  | typeof ACTIVE
  | typeof CANCELLED
  | typeof PROCESSING
  | typeof INCOMPLETE;

export interface GroupProductTypes {
  class?: string;
  currentPeriodEndDate?: string;
  groupId?: string;
  modificationDate?: string;
  paymentMethod?: string | null;
  productId: string;
  requestDate?: string;
  status: string;
  subscriptionId?: string;
  pricing?: PricesTypes[];
  promotionCode?: string | null;
  completePaymentUrl?: string;
}

export type InvoicePreviewItemTypes = {
  currency: Currency;
  amount: number | bigint | string;
  proration: boolean;
};

export type CouponTypes = {
  id: string;
  name: string;
  amountOff: number | null;
  percentOff: number | null;
  currency: null | Currency;
  duration: string;
  durationInMonths: null | number;
  appliesTo: null | string;
  metadata?: CouponMetadataTypes;
};
export type CouponModeType = typeof AUTOMATIC_COUPON | string;
export type CouponMetadataTypes = {
  interval?: string;
  mode?: CouponModeType;
  description?: string;
  is_proration_coupon?: string | boolean;
};

export type DiscountTypes =
  | {
      id: string;
      coupon: CouponTypes;
      promotionCode: string | null;
    }
  | undefined;

export interface InvoicePreviewTypes {
  currency: Currency;
  periodEndDate: string;
  subtotal?: string | number;
  totalExcludingTax?: number;
  tax?: string | number;
  amountDue: string | number;
  total: string | number;
  items: InvoicePreviewItemTypes[];
  discounts?: DiscountTypes[] | undefined;
  emailExcess?: number | null | undefined;
  startingBalance?: number;
  endingBalance?: number;
}

export type ProductCardProps = {
  key?: string;
  onPendingButtonAction?: (product: ProductTypes) => void;
  onIncompleteAction?: () => void;
  onActionButton?: (product: ProductTypes) => void | undefined;
  onProductSelect: (product: ProductTypes) => void;
  onRequestInfo?: (product: ProductTypes) => void;
  product: ProductTypes;
  userCurrency: Currency;
  userBillingMode?: BillingMode;
  activeBillingMode?: BillingMode | null;
  currentPeriodEndDate?: string;
  className?: string;
  formatProductName?: (productName: string | ProductFeaturesTypes) => string;
  formatCredits?: (product: ProductTypes) => React.ReactNode | string;
  formatCategoryName?: (productName: string | ProductFeaturesTypes) => string;
  alreadyPurchased?: boolean;
  alreadyPurchasedLink?: string;
  isCancelled?: boolean;
  isProcessing?: boolean;
  isPending?: boolean;
  isIncomplete?: boolean;
  actionButtonLabel?: string;
  hidePrice?: boolean;
  removePrice?: boolean;
  autoCheckout?: boolean;
  showFeatures?: boolean;
  isSticky?: boolean;
  promotionCode?: string | undefined;
};

export type iSteps = Record<string, string>;

export interface UserData {
  name: string;
  email: string;
  groupId: string;
  rolId: string;
  groupCreatedAt: string;
  lang: AppLanguages;
}

export interface iUserSetupData {
  lang: AppLanguages;
  productName: string;
  productId: string;
  currency: Currency;
}

export type Recharges = {
  id: string;
  requestDate: string;
  paymentDate: string;
  name: string;
  currency: Currency;
  interval: string | boolean;
  price: number | bigint;
  [key: string]: any;
};

export type StripeMethod = typeof STRIPE_METHOD_DIRECT | typeof STRIPE_METHOD_CHECKOUT;

export type responseStatus = typeof REQUEST_SUCCESS | typeof REQUEST_FAILED;

export interface Me {
  userInfo: UserInfo;
  groupInfo: GroupInfo;
  capabilities: Capabilities;
}

export interface UserInfo {
  id: string;
  name: string;
  lastName: string;
  email: string;
  rolId: string;
  language: AppLanguages;
}
export interface GroupInfo {
  id: string;
  name: string;
  createdAt: string;
  plan: string;
  countryId: string;
  currency: Currency;
}
export interface Capabilities {
  isManualValidationEnabled: boolean;
  isBillingDetailsFilled: boolean;
  canBuySubscriptionProducts: boolean;
  canBuyProducts: boolean;
  apiAccessEnabled: boolean;
  appsumo: Appsumo;
  chatbaseToken: string;
  isEmBlue: boolean;
}

export interface Appsumo {
  isAppsumo: boolean;
  canMakeAppsumoActions: boolean;
}

export interface iAuthInfo {
  localStorageKey: string;
  get: () => void;
  init: (e: any) => void;
  initFromLocalStorage: () => void;
  clear: () => void;
  save: () => void;
  getAccessToken: () => void;
  getUserName: () => string;
  getRolId: () => string;
  getUserId: () => string;
  getGroupId: () => string;
  getCompanyId: () => string;
  getCompanyName: () => void;
  getTimeZone: () => void;
  getCurrency: () => void;
  getCompanies: () => void;
  getGroupCompanies: () => void;
  getCreationTime: () => string;
  getExpirationTime: () => void;
  isExpired: () => void;
  isValid: () => void;
  getTimeToExpire: () => void;
  expiresInLessThan: (e: any) => void;
  isSuperAdmin: () => boolean;
  isAdminFreemium: () => boolean;
  isAdmin: () => boolean;
  isAdm: () => boolean;
  setCompanyData: (companyId: string) => void;
  getEntrypoint: () => string | undefined;
}

export type CurrencyObj = {
  iso: Currency;
  simbol: '€' | '$';
};

export interface iAuthInfoLocalStorage {
  access_token: string;
  companies: [];
  companiesByGroup: [];
  company_id: string | null;
  company_name: string | null;
  creation_time: string;
  currency: CurrencyObj;
  expires_in: string | number;
  group_id: string;
  rol_id: string;
  user_id: string;
  time_zone: string;
  user_name: string;
}

export type Theme = {
  assetsURL: string;
};

export interface ServiceTypes {
  service: string;
  rolId: string;
  properties: ServicePropertiesTypes | false;
  enable: boolean;
}

export type ServicePropertiesTypes = {
  companies: [];
  type: string;
  apiAccessEnabled: boolean;
};
